import {
  PlusOutlined,
  FileWordOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UserOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  EditOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import {
  message as Message,
  Upload,
  Button,
  Divider,
  Modal,
  Form,
  Input,
  notification,
  Dropdown,
  Checkbox,
  Select,
} from "antd";
import { webAPIs } from "./config/webAPIs";
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  memo,
  useCallback,
} from "react";
import { AuthContext } from "./AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, LevelFormat } from "docx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReactDOMServer from "react-dom/server";
import Close from "../src/assets/Icons/Close.png";
import Hamburger from "../src/assets/Icons/hamburger.png";
import { saveAs } from "file-saver";
import { subscriptionAlert } from "./utils";
import axiosInstance from "./config/axiosInstance";
import AssignModal from "./components/AssignModal";
import FilterModal from "./components/FilterModal";
import AssignAttributeModal from "./components/AssignAttributeModal";

// Utility: get file extension
const getExtension = (filename) => {
  const parts = filename.split(".");
  if (parts.length >= 3 && parts[parts.length - 1].toLowerCase() === "txt") {
    return parts[parts.length - 2].toLowerCase();
  }
  return parts[parts.length - 1].toLowerCase();
};

// File icon by extension
const getFileIcon = (extension) => {
  if (["mp3", "m4a", "m4p"].includes(extension))
    return <AudioOutlined className="text-[#D8AE5F]" />;
  if (["mp4"].includes(extension))
    return <VideoCameraOutlined className="text-[#D8AE5F]" />;
  return <FileWordOutlined className="text-[#D8AE5F]" />;
};

const FileItem = memo(({ item, onCheckboxChange, onDelete, isSelected }) => {
  const extension = getExtension(item?.originalName || "");
  return (
    <div className="group px-2 py-1.5 hover:bg-[#3f3f46] rounded-md transition-colors duration-200 flex justify-between items-center">
      <div className="flex items-center flex-1 min-w-0">
        <Checkbox
          checked={isSelected}
          onChange={() => onCheckboxChange(item.id)}
          className="mr-3"
          style={{
            "--ant-primary-color": "#D8AE5F",
            "--ant-primary-color-hover": "#E5BE76",
          }}
        />
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="flex items-center transition-transform duration-200 group-hover:scale-110">
            {getFileIcon(extension)}
          </span>
          <div className="overflow-hidden whitespace-nowrap text-ellipsis text-gray-200 group-hover:text-white transition-colors duration-200">
            {item?.originalName}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {item?.annoymisation && (
          <span className="text-[#D8AE5F] opacity-75">@</span>
        )}
        <DeleteOutlined
          className="text-gray-400 hover:text-red-400 transition-colors duration-200 opacity-0 group-hover:opacity-100 cursor-pointer"
          onClick={() => onDelete(item)}
        />
      </div>
    </div>
  );
});

const allowedTypes = {
  Free: {
    extensions: ["docx", "xlsx", "pdf"],
    mime: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/pdf",
    ],
  },
  Paid: {
    extensions: ["docx", "xlsx", "pdf", "mp4", "mp3", "m4a", "m4p"],
    mime: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/pdf",
      "audio/mp3",
      "audio/mpeg",
      "audio/mp4",
      "audio/m4a",
      "audio/x-m4a",
      "audio/mpga",
      "audio/wav",
      "audio/webm",
      "video/mp4",
    ],
  },
};

function isFileTypeAllowed(file, subscription) {
  const ext = file.name.split(".").pop().toLowerCase();
  const { extensions, mime } = allowedTypes[subscription];
  return extensions.includes(ext) || mime.includes(file.type);
}

const Sidebar = () => {
  const {
    user,
    handleResetMessage,
    files,
    setFiles,
    setLoading,
    message,
    selectedDocIds,
    setSelectedDocIds,
    projects,
    selectedProjectId,
    setSelectedProjectId,
    fetchProjects,
  } = useContext(AuthContext);

  const [visible, setVisible] = useState(false);
  const [flag, setFlag] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [classNameInsight, setClassNameInsight] = useState(
    "w-full flex items-center justify-center border-[#4d4d4f] text-white"
  );
  const [classNameAdmin, setClassNameAdmin] = useState(
    "w-full flex items-center justify-center border-[#4d4d4f] bg-stone-500 text-white"
  );
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [createProjectModal, setCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [assignAttributeModalOpen, setAssignAttributeModalOpen] =
    useState(false);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [lastFilteredFiles, setLastFilteredFiles] = useState(null);

  useEffect(() => {
    if (!selectedProjectId) {
      setAttributes([]);
      return;
    }
    axiosInstance
      .get(`${webAPIs.listAttributes}?projectId=${selectedProjectId}`)
      .then((res) => setAttributes(res.data || [])) // Ensure it's always an array
      .catch((err) => {
        console.error("Failed to fetch attributes:", err);
        setAttributes([]);
      });
  }, [selectedProjectId, assignModalVisible]);

  const acceptFileFormat = useMemo(
    () =>
      user?.subscriptionName === "Free"
        ? ".docx,.xlsx,.pdf"
        : ".docx,.xlsx,.pdf,.mp4,.mp3,.m4a,.m4p",
    [user]
  );

  // Generalized upload handler
  const getUploadProps = (annoymisation = false) => ({
    name: "file",
    multiple: true,
    action: webAPIs.addDocument,
    accept: acceptFileFormat,
    beforeUpload: (file) => {
      const subscription = user?.subscriptionName === "Free" ? "Free" : "Paid";
      if (!isFileTypeAllowed(file, subscription)) {
        Message.error(
          `File type not supported. Allowed: ${allowedTypes[
            subscription
          ].extensions.join(", ")}`
        );
        return false;
      }
      if (!selectedProjectId) {
        Message.error("Please select a project first.");
        return false;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("annoymisation", annoymisation);
      formData.append("projectId", selectedProjectId);
      setLoading(true);
      axiosInstance
        .post(webAPIs.addDocument, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          setLoading(false);
          Message.success(`${file.name} file uploaded successfully.`);
          const document = response.data.document;
          if (!files) {
            setFiles([document]);
            setSelectedDocIds([document.id]);
          } else if (!files.includes(document.fileName)) {
            setFiles([...files, document]);
            setSelectedDocIds((prev) => [...prev, document.id]);
          }
        })
        .catch((error) => {
          setLoading(false);
          const message = error.response?.data?.error || error.message || "Upload failed";
          Message.error(`${file.name} file upload failed. ${message}`);
        });
      return false;
    },
  });

  const props = getUploadProps(false);
  const annoymisationProps = getUploadProps(true);

  const handleClick = useCallback(() => {
    setVisible(true);
    setIsOpen(false);
  }, []);

  const handleRemoveFile = useCallback(async (data) => {
    await axiosInstance.post(webAPIs.changeDocument, { file: data });
  }, []);

  // DOCX helpers for Markdown-ish export
  const createText = (text, opts = {}) =>
    new TextRun({ text, size: 22, font: "Arial", ...opts });
  const createParagraph = (children, extra = {}) =>
    new Paragraph({ children, spacing: { before: 60, after: 60 }, ...extra });
  const createEmptyParagraph = () => new Paragraph({});

  const inlineTokenizer = (text) => {
    const parts = [];
    const pattern = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|~~[^~]+~~)/g;
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const plain = text.slice(lastIndex, match.index);
        if (plain) parts.push(createText(plain));
      }
      const token = match[0];
      if (token.startsWith("`") && token.endsWith("`")) {
        parts.push(new TextRun({ text: token.slice(1, -1), size: 22, font: "Courier New" }));
      } else if ((token.startsWith("**") && token.endsWith("**")) || (token.startsWith("__") && token.endsWith("__"))) {
        parts.push(createText(token.slice(2, -2), { bold: true }));
      } else if ((token.startsWith("*") && token.endsWith("*")) || (token.startsWith("_") && token.endsWith("_"))) {
        parts.push(createText(token.slice(1, -1), { italics: true }));
      } else if (token.startsWith("~~") && token.endsWith("~~")) {
        parts.push(createText(token.slice(2, -2), { strike: true }));
      }
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < text.length) {
      const rest = text.slice(lastIndex);
      if (rest) parts.push(createText(rest));
    }
    return parts;
  };

  const parseMarkdownToParagraphs = (markdown) => {
    const paragraphs = [];
    const lines = (markdown || "").split(/\r?\n/);
    let inCodeBlock = false;
    let codeBuffer = [];

    const flushCode = () => {
      if (codeBuffer.length === 0) return;
      for (const codeLine of codeBuffer) {
        paragraphs.push(
          createParagraph([
            new TextRun({ text: codeLine, size: 22, font: "Courier New" }),
          ])
        );
      }
      codeBuffer = [];
    };

    for (const raw of lines) {
      const line = raw.replace(/\t/g, "    ");
      const trimmed = line.trim();
      // code fences
      if (trimmed.startsWith("```") || trimmed.startsWith("~~~")) {
        if (!inCodeBlock) {
          inCodeBlock = true;
          continue;
        } else {
          inCodeBlock = false;
          flushCode();
          continue;
        }
      }
      if (inCodeBlock) {
        codeBuffer.push(line);
        continue;
      }

      if (trimmed === "" ) { paragraphs.push(createEmptyParagraph()); continue; }

      // headings
      const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (h) {
        const level = Math.min(h[1].length, 6);
        const contentRuns = inlineTokenizer(h[2]);
        paragraphs.push(
          new Paragraph({
            heading: [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3, HeadingLevel.HEADING_4, HeadingLevel.HEADING_5, HeadingLevel.HEADING_6][level - 1],
            children: contentRuns,
            spacing: { before: 120, after: 60 },
          })
        );
        continue;
      }

      // horizontal rule
      if (/^\s*(-{3,}|_{3,}|\*{3,})\s*$/.test(trimmed)) {
        paragraphs.push(new Paragraph({ children: [createText("")], border: { top: { color: "999999", space: 1, size: 6 } } }));
        continue;
      }

      // blockquote
      const bq = trimmed.match(/^>\s?(.*)$/);
      if (bq) {
        const runs = inlineTokenizer(bq[1]);
        paragraphs.push(createParagraph(runs, { indent: { left: 720 }, border: { left: { color: "777777", size: 6 } } }));
        continue;
      }

      // lists
      const ul = line.match(/^(\s*)[-*+]\s+(.*)$/);
      if (ul) {
        const indentSpaces = ul[1].length;
        const level = Math.min(Math.floor(indentSpaces / 2), 2);
        const runs = inlineTokenizer(ul[2]);
        paragraphs.push(new Paragraph({ children: runs, numbering: { reference: "unordered-list", level }, spacing: { before: 0, after: 60 } }));
        continue;
      }
      const ol = line.match(/^(\s*)\d+[.)]\s+(.*)$/);
      if (ol) {
        const indentSpaces = ol[1].length;
        const level = Math.min(Math.floor(indentSpaces / 2), 2);
        const runs = inlineTokenizer(ol[2]);
        paragraphs.push(new Paragraph({ children: runs, numbering: { reference: "ordered-list", level }, spacing: { before: 0, after: 60 } }));
        continue;
      }

      // plain paragraph
      const runs = inlineTokenizer(line);
      paragraphs.push(createParagraph(runs));
    }

    // flush if file ended in code block (defensive)
    flushCode();
    return paragraphs;
  };

  const convertMessageToParagraphs = useCallback(async (messages, children) => {
    for (const [index, item] of messages.entries()) {
      const runsLabel = [createText(item.role === "user" ? "User:" : "Assistant:", { bold: true })];
      children.push(createParagraph(runsLabel));
      const paras = parseMarkdownToParagraphs(item.content || "");
      for (const p of paras) children.push(p);
      if (index !== messages.length - 1) children.push(createEmptyParagraph());
    }
  }, []);

  const handleOutputConveration = useCallback(async () => {
    try {
      const exportStyles = `
        body { font-family: Arial, sans-serif; }
        .markdown-body { color: #000; line-height: 1.6; }
        .markdown-body h1 { font-size: 24px; margin: 1em 0 0.5em; }
        .markdown-body h2 { font-size: 20px; margin: 1em 0 0.5em; }
        .markdown-body h3 { font-size: 18px; margin: 1em 0 0.5em; }
        .markdown-body p { margin: 0.25rem 0 0.75rem; }
        .markdown-body ul, .markdown-body ol { padding-left: 1.25rem; margin: 0.5rem 0 1rem; }
        .markdown-body code { background: #f2f2f2; padding: 0.1rem 0.3rem; border-radius: 3px; }
        .markdown-body pre { background: #f6f6f6; padding: 0.75rem 1rem; border-radius: 6px; overflow-x: auto; }
        .markdown-body blockquote { border-left: 4px solid #bbb; margin: 0.75rem 0; padding: 0.25rem 0.75rem; color: #333; background: #fafafa; }
        .role-label { font-weight: bold; margin: 0.5rem 0 0.25rem; }
      `;

      const element = (
        <div className="markdown-body">
          {message.map((item, idx) => (
            <div key={idx}>
              <div className="role-label">{item.role === "user" ? "User" : "Assistant"}</div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content || ""}</ReactMarkdown>
            </div>
          ))}
        </div>
      );

      const htmlBody = ReactDOMServer.renderToStaticMarkup(element);
      console.log({htmlBody})
      const wrapHtml = (body) => `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>${exportStyles}</style></head><body>${body}</body></html>`;

      const inlineImagesInHtml = async (html) => {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const images = Array.from(doc.querySelectorAll('img'));
          for (const img of images) {
            const src = img.getAttribute('src');
            if (!src || src.startsWith('data:')) continue;
            try {
              const res = await fetch(src, { mode: 'cors' });
              const blob = await res.blob();
              const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
              img.setAttribute('src', dataUrl);
            } catch (e) {
              // leave as-is on failure
            }
          }
          return wrapHtml(doc.body.innerHTML);
        } catch {
          return wrapHtml(html);
        }
      };

      const fullHtml = await inlineImagesInHtml(htmlBody);

      // Use html-to-docx (requires webpack fallbacks configured)
      const mod = await import('html-to-docx');
      const htmlToDocx = mod.default || mod;
      const blob = await htmlToDocx(fullHtml);
      saveAs(blob, 'Output.docx');
    } catch (err) {
      // Fallback to previous DOCX path if html-to-docx is not installed
      try {
        const children = [];
        await convertMessageToParagraphs(message, children);
        const doc = await new Document({
          numbering: {
            config: [
              { reference: 'unordered-list', levels: [
                { level: 0, format: LevelFormat.BULLET, text: '\u2022', alignment: AlignmentType.START },
                { level: 1, format: LevelFormat.BULLET, text: '\u25E6', alignment: AlignmentType.START },
                { level: 2, format: LevelFormat.BULLET, text: '\u25AA', alignment: AlignmentType.START },
              ]},
              { reference: 'ordered-list', levels: [
                { level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.START },
                { level: 1, format: LevelFormat.LOWER_LETTER, text: '%2.', alignment: AlignmentType.START },
                { level: 2, format: LevelFormat.LOWER_ROMAN, text: '%3.', alignment: AlignmentType.START },
              ]},
            ],
          },
          sections: [{ children: children.flat(Infinity).filter(Boolean) }],
        });
        Packer.toBlob(doc).then((blob) => saveAs(blob, 'Output.docx'));
      } catch (e) {
        notification.error({ description: 'Failed to export Word document. Please install and configure html-to-docx.', duration: 2 });
      }
    }
  }, [message, convertMessageToParagraphs]);

  const onFinish = async () => {
    form.submit();
    try {
      await axiosInstance.post(webAPIs.changePassword, {
        password: form.getFieldValue("newPassword"),
        user: user.id,
      });
      setVisible(false);
      form.setFieldsValue({ newPassword: "", confirm: "" });
      notification.success({
        description: "Change Password Successful",
        duration: 1,
      });
    } catch (error) {
      notification.error({ description: "Internal Server Error" });
    }
  };

  const handleClearConversation = useCallback(
    async (data = []) => {
      const projectId = selectedProjectId;
      await axiosInstance.post(
        `${webAPIs.clearConversation}?projectId=${projectId}`,
        { conversation: data }
      );
      handleResetMessage();
    },
    [selectedProjectId, handleResetMessage]
  );

  // Navigation helpers
  const handleKGraph = () => {
    if (user?.subscriptionName === "Free") return subscriptionAlert();
    setFlag(true);
    navigate("knowledge");
  };
  const handleQuickPrompt = () => {
    if (user?.subscriptionName === "Free") return subscriptionAlert();
    navigate("quick-prompt");
  };

  // Project CRUD
  const handleCreateProject = async () => {
    if (!newProjectName.trim())
      return Message.error("Project name is required");
    try {
      const response = await axiosInstance.post(webAPIs.createProject, {
        accountId: user.accountId,
        name: newProjectName.trim(),
      });
      const data = await response.data;
      if (data.id) {
        await fetchProjects(user.accountId);
        setSelectedProjectId(data.id);
        setCreateProjectModal(false);
        setNewProjectName("");
        Message.success("Project created");
      } else {
        Message.error(data.error || "Failed to create project");
      }
    } catch {
      Message.error("Failed to create project");
    }
  };
  const handleEditProject = async (projectId, newName) => {
    if (!newName.trim()) return Message.error("Project name is required");
    try {
      await axiosInstance.put(webAPIs.updateProject(projectId), {
        name: newName.trim(),
      });
      await fetchProjects(user.accountId);
      Message.success("Project updated");
    } catch {
      Message.error("Failed to update project");
    }
  };
  const handleDeleteProject = async (projectId) => {
    try {
      await axiosInstance.delete(webAPIs.deleteProject(projectId));
      await fetchProjects(user.accountId);
      if (selectedProjectId === projectId && projects.length > 1) {
        const nextProject = projects.find((p) => p.id !== projectId);
        if (nextProject) setSelectedProjectId(nextProject.id);
      }
      Message.success("Project deleted");
    } catch {
      Message.error("Failed to delete project");
    }
  };

  // Update button styles on route/flag change
  useEffect(() => {
    setClassNameInsight(
      location.pathname === "/dashboard/quick-prompt" || flag
        ? "w-full flex items-center justify-center border-[#4d4d4f] text-white"
        : "w-full flex items-center justify-center border-[#4d4d4f] bg-activebuttonColor text-white btn-insight"
    );
    setClassNameAdmin(
      location.pathname === "/dashboard/quick-prompt" || flag
        ? "w-full flex items-center justify-center border-[#4d4d4f] bg-activebuttonColor text-white"
        : "w-full flex items-center justify-center border-[#4d4d4f] text-white"
    );
  }, [flag, location.pathname]);

  // Fetch files for the selected project
  const fetchFilesForProject = useCallback(
    async (projectIdOverride) => {
      const pid = projectIdOverride || selectedProjectId;
      if (!pid) return [];
      try {
        const response = await axiosInstance.get(
          `${webAPIs.getConversation}?projectId=${pid}`
        );
        if (response.data.success && response.data.result.files) {
          const filesWithProject = response.data.result.files.map((f) => ({
            ...f,
            projectId: pid,
          }));
          setFiles(filesWithProject);
          return filesWithProject;
        } else {
          setFiles([]);
          return [];
        }
      } catch {
        setFiles([]);
        return [];
      }
    },
    [selectedProjectId, setFiles]
  );

  useEffect(() => {
    fetchFilesForProject();
  }, [selectedProjectId, fetchFilesForProject]);

  useEffect(() => {
    console.log("Files updated:", files);
    setFilteredFiles(files);
  }, [files]);
  
  const items = [
    {
      key: "1",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => {
            if (user?.subscriptionName === "Free") {
              subscriptionAlert();
            } else {
              navigate("transcript");
            }
          }}
        >
          View Transcripts
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => {
            if (user?.subscriptionName === "Free") {
              subscriptionAlert();
            } else {
              navigate("scrape");
            }
          }}
        >
          Scrape URLs
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => {
            handleKGraph();
          }}
        >
          K-Graph
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => handleClearConversation()}
        >
          Clear Chat
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={handleClick}
        >
          Change password
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => setAssignAttributeModalOpen(true)}
        >
          Manage Attributes
        </Button>
      ),
    },
    {
      key: "7",
      label: (
        <Button
          className="w-full flex items-center justify-center rounded-lg bg-sidebarColor text-white"
          onClick={() => setAssignModalVisible(true)}
        >
          Assign Attribute Level
        </Button>
      ),
    },
  ];

  const handleNavigation = useCallback(
    (path) => {
      if (user?.subscriptionName === "Free") {
        subscriptionAlert();
      } else {
        navigate(path);
        setIsOpen(false);
      }
    },
    [user?.subscriptionName, navigate]
  );

  const handleOutputChat = useCallback(() => {
    handleOutputConveration();
    setIsOpen(false);
  }, [handleOutputConveration]);

  const handleClearChat = useCallback(() => {
    handleClearConversation();
    setIsOpen(false);
  }, [handleClearConversation]);

  const handleChangePassword = useCallback(() => {
    handleClick();
    setIsOpen(false);
  }, [handleClick]);

  const mobileItems = [
    {
      key: "1",
      label: (
        <Upload
          {...props}
          accept={acceptFileFormat}
          className="w-full items-center flex"
        >
          <span
            className="w-full text-center text-white nav-item"
            onClick={() => setIsOpen(!isOpen)}
          >
            Add File
          </span>
        </Upload>
      ),
    },
    {
      key: "2",
      label: (
        <Upload
          {...annoymisationProps}
          accept={acceptFileFormat}
          className="w-full items-center flex"
        >
          <span
            className="w-full text-center text-white nav-item"
            onClick={() => setIsOpen(!isOpen)}
          >
            Add File & Anonymise
          </span>
        </Upload>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          className="w-full text-center text-white nav-item"
          onClick={handleOutputChat}
        >
          Output Chat
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          className={`w-full text-center ${
            location.pathname === "/dashboard/transcript"
              ? "text-[#D8AE5F]"
              : "text-white"
          } nav-item`}
          onClick={() => handleNavigation("transcript")}
        >
          View Transcripts
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          className={`w-full text-center ${
            location.pathname === "/dashboard/scrape"
              ? "text-[#D8AE5F]"
              : "text-white"
          } nav-item`}
          onClick={() => handleNavigation("scrape")}
        >
          Scrape URLs
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          className={`w-full text-center ${
            location.pathname === "/dashboard/quick-prompt"
              ? "text-[#D8AE5F]"
              : "text-white"
          } nav-item`}
          onClick={() => handleNavigation("quick-prompt")}
        >
          Quick Prompt
        </Button>
      ),
    },
    {
      key: "7",
      label: (
        <Button
          className={`w-full text-center ${
            location.pathname === "/dashboard/knowledge"
              ? "text-[#D8AE5F]"
              : "text-white"
          } nav-item`}
          onClick={() => handleNavigation("knowledge")}
        >
          K Graph
        </Button>
      ),
    },
    {
      key: "8",
      label: (
        <Button
          className="w-full text-center text-white nav-item"
          onClick={handleClearChat}
        >
          Clear Chat
        </Button>
      ),
    },
    {
      key: "9",
      label: (
        <Button
          className="w-full text-center text-white nav-item"
          onClick={handleChangePassword}
        >
          Change Password
        </Button>
      ),
    },
    {
      key: "10",
      label: (
        <Button
          className={`w-full text-center ${
            location.pathname === "/dashboard/admin/subscriptions"
              ? "text-[#D8AE5F]"
              : "text-white"
          } nav-item`}
          onClick={() => handleNavigation("admin/subscriptions")}
        >
          Change Your Plan
        </Button>
      ),
    },
  ];

  const indeterminate =
    files && selectedDocIds.length > 0 && selectedDocIds.length < files.length;
  const checkAll =
    files && files.length > 0 && selectedDocIds.length === files.length;

  const onCheckAllChange = useCallback(
    (e) => {
      const newSelectedIds = e.target.checked
        ? files.map((file) => file.id)
        : [];
      setSelectedDocIds(newSelectedIds);
    },
    [files, setSelectedDocIds]
  );

  const handleCheckboxChange = useCallback(
    (id) => {
      setSelectedDocIds((prev) =>
        prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]
      );
    },
    [setSelectedDocIds]
  );

  const handleFileDelete = useCallback(
    async (item) => {
      setFiles(files.filter((it) => it !== item));
      setSelectedDocIds((prev) => prev.filter((id) => id !== item.id));
      await handleRemoveFile(item);
    },
    [files, handleRemoveFile, setFiles, setSelectedDocIds]
  );

  const projectActions = [
    {
      key: "edit",
      label: "Edit name",
      icon: <EditOutlined />,
      onClick: () => {
        const project = projects.find((p) => p.id === selectedProjectId);
        const newName = prompt("Enter new project name:", project?.name || "");
        if (newName && newName.trim()) {
          handleEditProject(selectedProjectId, newName.trim());
        }
      },
    },
    {
      key: "delete",
      label: "Delete project",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        if (
          window.confirm(
            "Are you sure you want to delete this project? This action cannot be undone."
          )
        ) {
          handleDeleteProject(selectedProjectId);
        }
      },
    },
  ];

  return (
    <div className="sidebar sm:h-full h-[84px] bg-[#464646] sm:bg-sidebarColor flex flex-col p-3 overflow-y-auto">
      <div className="hidden sm:flex sm:flex-col sm:h-full">
        <div className="flex flex-col justify-center items-center gap-3">
          <div className="flex gap-3 py-0 w-full sidebar-top-buttons">
            <Button
              className={classNameInsight}
              onClick={() => {
                setFlag(false);
                navigate("/dashboard");
              }}
            >
              Prompt
            </Button>
            <Button
              className={classNameAdmin}
              onClick={() => {
                handleQuickPrompt();
              }}
            >
              Quick Prompt
            </Button>
          </div>
          <div className="flex gap-1 py-0 w-full">
            <Upload
              {...props}
              accept={acceptFileFormat}
              className="custombutton w-full"
            >
              <Button className="text-md py-1 h-max text-white flex items-center justify-center w-full border-[#4d4d4f]">
                <PlusOutlined />
                Add File
              </Button>
            </Upload>
          </div>
          <div className="flex gap-1 py-0 w-full">
            <Upload
              {...annoymisationProps}
              accept={acceptFileFormat}
              className="custombutton w-full"
            >
              <Button className="text-md py-1 h-max text-white flex items-center justify-center w-full border-[#4d4d4f]">
                <PlusOutlined />
                Add File & Anonymise
              </Button>
            </Upload>
          </div>
          <div className="flex gap-1 py-0 w-full">
            <Button
              className="w-full flex items-center justify-center border-[#4d4d4f] text-white"
              onClick={() => handleOutputConveration()}
            >
              <DownloadOutlined />
              Output Chat
            </Button>
          </div>
          <div className="flex gap-1 py-0 w-full">
            <Dropdown
              menu={{
                items,
              }}
              placement="bottom"
            >
              <Button className="w-full flex items-center justify-center border-[#4d4d4f] text-white">
                More Options …
              </Button>
            </Dropdown>
          </div>
          <div className="flex gap-1 py-0 w-full">
            <Button
              className="w-full flex items-center justify-center border-[#4d4d4f] text-white"
              onClick={() => setFilterModalVisible(true)}
            >
              Filter Documents
            </Button>
          </div>
        </div>
        <Divider className="bg-[#d8ae5f] my-5" />
        <div className="flex-grow flex flex-col h-1/3 overflow-y-auto">
          {filteredFiles && filteredFiles.length > 0 ? (
            <>
              <div className="px-2 mb-2">
                <Checkbox
                  indeterminate={indeterminate}
                  onChange={onCheckAllChange}
                  checked={checkAll}
                  style={{
                    "--ant-primary-color": "#D8AE5F",
                    "--ant-primary-color-hover": "#E5BE76",
                  }}
                >
                  <span className="text-gray-200">Select All Files</span>
                </Checkbox>
              </div>
              <Divider className="my-2 bg-[#4d4d4f]" />
              <div className="space-y-0.5">
                {filteredFiles.map((item) => (
                  <FileItem
                    key={item.id}
                    item={item}
                    isSelected={selectedDocIds.includes(item.id)}
                    onCheckboxChange={handleCheckboxChange}
                    onDelete={handleFileDelete}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
              <FileWordOutlined className="text-2xl mb-2" />
              <span>No files uploaded</span>
            </div>
          )}
        </div>
        <Divider className="bg-[#d8ae5f] my-2" />
        <div className="sidebar-footer flex flex-col justify-center text-md text-white">
          <div className="acc-item !text-md py-1 flex items-center justify-center truncate">
            <UserOutlined className="pr-2" />
            {user?.email}
          </div>
          <div className="acc-item text-lg py-1 mb-1 flex items-center justify-center truncate gap-2">
            <div className="plan-title acc-item text-sm truncate">
              Your Plan:
            </div>
            <div className="plan-name acc-item !text-lg truncate">
              {user?.subscriptionName}
            </div>
          </div>
          {user?.subscriptionName === "Free Trial" && user?.daysLeft && (
            <div className="plan-expires acc-item text-lg flex items-center justify-center truncate">
              Your Trial expires in {user?.daysLeft} days
            </div>
          )}
          <Button
            className="text-md py-1 h-max text-white flex items-center justify-center w-full border-[#4d4d4f] mb-[10px]"
            onClick={() => {
              if (
                user?.subscriptionName === "Advanced" ||
                user?.subscriptionName === "Enterprise"
              ) {
                window.location.href = "mailto:info@insight-lab.ai";
              } else {
                navigate("/dashboard/admin/subscriptions");
              }
            }}
          >
            {user?.subscriptionName === "Enterprise"
              ? "Contact Sales"
              : "Upgrade"}
          </Button>
          {user?.role === 3 && (
            <Button
              className="py-3 flex items-center justify-center border-[#4d4d4f] text-white"
              onClick={() => navigate("superadmin")}
            >
              Super Admin
            </Button>
          )}
        </div>

        {/* START: Professional Project Management Section */}
        <Divider className="bg-[#d8ae5f] my-4" />
        <div className="flex flex-col gap-3 px-1">
          <div className="flex justify-between items-center">
            <span className="text-white text-md font-medium">Project</span>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setCreateProjectModal(true)}
              className="flex items-center justify-center text-[#D8AE5F] border-[#D8AE5F] hover:!text-white hover:!bg-[#D8AE5F] hover:!border-[#D8AE5F]"
              ghost
            >
              Create Project
            </Button>
          </div>
          <div className="flex items-center gap-2 w-full">
            <Select
              value={selectedProjectId}
              onChange={setSelectedProjectId}
              options={projects.map((p) => ({ label: p.name, value: p.id }))}
              placeholder="Select a project"
              className="flex-grow"
              dropdownStyle={{ zIndex: 2000 }}
            />
            <Dropdown
              menu={{ items: projectActions }}
              trigger={["click"]}
              placement="topRight"
              disabled={!selectedProjectId}
            >
              <Button
                icon={<EllipsisOutlined />}
                className="flex justify-center items-center border-[#4d4d4f] text-white"
              />
            </Dropdown>
          </div>
        </div>
        {/* END: Professional Project Management Section */}
      </div>
      <div className="contents sm:hidden">
        <div className="relative">
          <div className="flex justify-between items-center px-2">
            <img
              src={`${process.env.PUBLIC_URL}/logo-white.png`}
              alt="logo"
              className="h-14"
            />
            <span onClick={() => setIsOpen(!isOpen)}>
              <img src={isOpen ? Close : Hamburger} alt="open" />
            </span>
          </div>
          {isOpen && (
            <div className="fixed right-0 mt-3 w-full bg-[#3f3f46] text-white z-20 border-t border-[#D8AE5F]">
              <ul className="flex flex-col w-full gap-2 justify-center px-5 py-5">
                {mobileItems.map((item) => (
                  <li key={item.key}>{item.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <Modal
        open={visible}
        onOk={onFinish}
        onCancel={() => setVisible(false)}
        okButtonProps={{ className: "bg-[#007bff]" }}
      >
        <div className="mt-2 mb-4 text-xl">Change Password</div>
        <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} form={form}>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              {
                required: true,
                message: "Please enter your new password",
              },
              {
                min: 6,
                message: "Password must be at least 6 characters long",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: "Please confirm your new password",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords that you entered do not match")
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={createProjectModal}
        onOk={handleCreateProject}
        onCancel={() => setCreateProjectModal(false)}
        okButtonProps={{ className: "bg-[#007bff]" }}
        title="Create New Project"
      >
        <Input
          placeholder="Project Name"
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          onPressEnter={handleCreateProject}
        />
      </Modal>
      <AssignModal
        visible={assignModalVisible}
        onClose={() => setAssignModalVisible(false)}
        files={files}
        attributes={attributes}
        projectId={selectedProjectId}
        onAssigned={() => {
          // Optionally refresh assignments or files
        }}
      />
      <FilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        attributes={attributes}
        projectId={selectedProjectId}
        onFiltered={(filteredData, newFilters) => {
          console.log({filteredData, newFilters})
          setFilteredFiles(filteredData);
          setLastFilteredFiles(filteredData);
          setAppliedFilters(newFilters);
        }}
        fetchFilesForProject={fetchFilesForProject}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        lastFilteredFiles={lastFilteredFiles}
      />
      <AssignAttributeModal
        open={assignAttributeModalOpen}
        onClose={() => setAssignAttributeModalOpen(false)}
        projectId={selectedProjectId}
      />
    </div>
  );
};

export default Sidebar;
